import { useState } from 'react'
import { supabase } from '../supabaseClient'

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export default function EditAssignmentModal({ assignment, type, onClose, onSaved }) {
  const isExercise = type === 'exercise'
  const item = isExercise ? assignment.exercises : assignment.interventions

  const [sets, setSets] = useState(assignment.sets || item?.sets || '')
  const [reps, setReps] = useState(assignment.reps || item?.reps || '')
  const [duration, setDuration] = useState(assignment.duration_seconds || item?.duration_seconds || '')
  const [frequency, setFrequency] = useState(assignment.frequency || 'daily')
  const [selectedDays, setSelectedDays] = useState(assignment.days_of_week || [])
  const [numWeeks, setNumWeeks] = useState(assignment.num_weeks || '')
  const [note, setNote] = useState(assignment.clinician_note || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function toggleDay(day) {
    setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort())
  }

  async function handleSave() {
    setSaving(true)
    setError('')

    const table = isExercise ? 'assignments' : 'intervention_assignments'
    const updates = {
      frequency,
      days_of_week: frequency === 'custom' ? selectedDays : null,
      num_weeks: numWeeks ? parseInt(numWeeks) : null,
      clinician_note: note || null
    }

    if (isExercise) {
      updates.sets = sets ? parseInt(sets) : null
      updates.reps = reps ? parseInt(reps) : null
      updates.duration_seconds = duration ? parseInt(duration) : null
    }

    const { error: updateError } = await supabase
      .from(table)
      .update(updates)
      .eq('id', assignment.id)

    if (updateError) { setError(updateError.message); setSaving(false); return }
    onSaved()
    onClose()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: 'var(--surface)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 640, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 17, margin: 0 }}>Edit {isExercise ? 'exercise' : 'task'}</h2>
          <button type="button" className="secondary" onClick={onClose} style={{ fontSize: 13, padding: '6px 12px' }}>Cancel</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 15, margin: '0 0 1.5rem' }}>{item?.title}</p>

          {error && <div className="alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

          <div className="form-group">
            {isExercise && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div className="form-field">
                  <label className="form-label" style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>Sets</label>
                  <input type="number" placeholder="3" min="1" value={sets} onChange={e => setSets(e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-label" style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>Reps</label>
                  <input type="number" placeholder="10" min="1" value={reps} onChange={e => setReps(e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-label" style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>Duration (sec)</label>
                  <input type="number" placeholder="30" min="1" value={duration} onChange={e => setDuration(e.target.value)} />
                </div>
              </div>
            )}

            <div className="form-field">
              <label className="form-label">Schedule</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => setFrequency('daily')} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8, background: frequency === 'daily' ? 'var(--brand)' : 'var(--surface)', color: frequency === 'daily' ? 'white' : 'var(--text-primary)', border: frequency === 'daily' ? 'none' : '1.5px solid var(--border)' }}>Daily</button>
                <button type="button" onClick={() => setFrequency('custom')} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8, background: frequency === 'custom' ? 'var(--brand)' : 'var(--surface)', color: frequency === 'custom' ? 'white' : 'var(--text-primary)', border: frequency === 'custom' ? 'none' : '1.5px solid var(--border)' }}>Specific days</button>
              </div>
            </div>

            {frequency === 'custom' && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DAY_LABELS.map((label, i) => (
                  <button key={i} type="button" onClick={() => toggleDay(i)} style={{ width: 42, height: 42, borderRadius: '50%', fontSize: 12, fontWeight: 600, padding: 0, background: selectedDays.includes(i) ? 'var(--brand)' : 'var(--surface)', color: selectedDays.includes(i) ? 'white' : 'var(--text-secondary)', border: selectedDays.includes(i) ? 'none' : '1.5px solid var(--border)' }}>{label}</button>
                ))}
              </div>
            )}

            <div className="form-field">
              <label className="form-label">Number of weeks</label>
              <input type="number" placeholder="e.g. 4" min="1" max="52" value={numWeeks} onChange={e => setNumWeeks(e.target.value)} />
            </div>

            <div className="form-field">
              <label className="form-label">Clinician note</label>
              <input placeholder="Optional note for patient" value={note} onChange={e => setNote(e.target.value)} />
            </div>

            <button onClick={handleSave} disabled={saving} style={{ padding: 14, fontSize: 15 }}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
